import { useState } from 'react'
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient } from '@/api/client'
import type { AppError } from '@/api/errors'
import { useWorkspaceStore } from '@/stores/workspace'
import { Spinner } from '@/components/ui/spinner'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import type { Project } from '@/features/projects/project'
import { RowsTable } from '@/features/projects/RowsTable'
import { TasksSection } from '@/features/projects/TasksSection'
import { ProjectSettingsBar } from '@/features/projects/ProjectSettingsBar'
import { ProjectActionsMenu } from '@/features/projects/ProjectActionsMenu'
import { RenameProjectDialog } from '@/features/projects/RenameProjectDialog'
import { useUpdateProjectSettings } from '@/features/projects/useUpdateProjectSettings'
import { useDeleteProject } from '@/features/projects/useDeleteProject'
import { useDuplicateProject } from '@/features/projects/useDuplicateProject'
import { useImportZipPicker } from '@/features/projects/ImportZipButton'
import { ExportProjectDialog } from '@/features/projects/ExportProjectDialog'
import { ColumnsDialog } from '@/features/projects/ColumnsDialog'
import { useColumnSettings } from '@/features/projects/useColumnSettings'
import '@/features/projects/projectV2.css'

export const Route = createFileRoute('/_authenticated/projects_/$id')({
  component: ProjectDetailPage,
})

/**
 * `/projects/:id` — 1:1 visual port of legacy project-v2.html
 *   A. .p2-header  — back link / title / actions
 *   B. .p2-summary — six counters
 *   C. .p2-launch-panel — ProjectSettingsBar
 *   D. .p2-section + .p2-table — RowsTable
 *   E. .p2-tasks-header + .p2-section — TasksSection (QT-look)
 */
function ProjectDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams({ from: '/_authenticated/projects_/$id' })
  const wsId = useWorkspaceStore((s) => s.activeWsId)
  const numericId = Number(id)
  const [tableSignal, setTableSignal] = useState<{
    kind: 'validate' | 'launch_audio' | 'launch_full' | 'stop' | 'add' | 'import'
    nonce: number
  } | null>(null)
  const [rowsCount, setRowsCount] = useState(0)
  const [summary, setSummary] = useState<SummaryCounts>(emptySummary)
  const [launchPending, setLaunchPending] = useState(false)

  const [renameOpen, setRenameOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [columnsOpen, setColumnsOpen] = useState(false)

  const updateSettings = useUpdateProjectSettings(numericId)
  const deleteProj = useDeleteProject()
  const duplicateProj = useDuplicateProject()

  const importZip = useImportZipPicker(numericId)
  const columnSettings = useColumnSettings(numericId)

  const query = useQuery<Project, AppError>({
    queryKey: ['ws', wsId, 'projects', 'detail', numericId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<Project>(`/projects/${numericId}`)
      return data
    },
    // No wsId guard: the backend's AccessFilter resolves the personal
    // context (workspace_id IS NULL) from the auth token, so users
    // without a workspace (activeWsId === null) must still load detail
    // instead of hanging on a spinner forever. Mirrors useProjects().
    enabled: Number.isFinite(numericId),
  })

  const send = (kind: NonNullable<typeof tableSignal>['kind']) =>
    setTableSignal({ kind, nonce: Date.now() })

  const onRenameSubmit = (newTitle: string) => {
    updateSettings.mutate(
      { title: newTitle },
      {
        onSuccess: () => {
          setRenameOpen(false)
          toast.success(
            t('projects.p2.msg_rename_saved', { defaultValue: 'Назву збережено' }),
          )
        },
        onError: () =>
          toast.error(
            t('projects.actions.rename_error', { defaultValue: 'Не вдалося перейменувати' }),
          ),
      },
    )
  }

  const onArchiveConfirm = () => {
    if (!query.data) return
    const next = query.data.status === 'archived' ? 'active' : 'archived'
    updateSettings.mutate(
      { status: next },
      {
        onSuccess: () => {
          setArchiveOpen(false)
          toast.success(
            next === 'archived'
              ? t('projects.p2.msg_project_archived', { defaultValue: 'Проєкт архівовано' })
              : t('projects.actions.unarchived', { defaultValue: 'Проєкт розархівовано' }),
          )
        },
        onError: () =>
          toast.error(
            t('projects.p2.msg_archive_err', { defaultValue: 'Не вдалося архівувати проєкт' }),
          ),
      },
    )
  }

  const onDeleteConfirm = () => {
    deleteProj.mutate(
      { id: numericId },
      {
        onSuccess: () => {
          setDeleteOpen(false)
          toast.success(
            t('projects.p2.msg_project_deleted', { defaultValue: 'Проєкт видалено' }),
          )
          void navigate({ to: '/projects' })
        },
        onError: () =>
          toast.error(
            t('projects.p2.msg_delete_proj_err', { defaultValue: 'Не вдалося видалити проєкт' }),
          ),
      },
    )
  }

  const onDuplicate = () => {
    duplicateProj.mutate(
      { id: numericId },
      {
        onSuccess: (created) => {
          toast.success(
            t('projects.p2.msg_project_duplicated', { defaultValue: 'Проєкт продубльовано' }),
          )
          void navigate({ to: '/projects/$id', params: { id: String(created.id) } })
        },
        onError: () =>
          toast.error(
            t('projects.p2.msg_dup_err', { defaultValue: 'Не вдалося дублювати проєкт' }),
          ),
      },
    )
  }

  return (
    <div className="p2-page -m-4">
      {query.isPending && (
        <div className="flex items-center gap-2 text-text-secondary p-6">
          <Spinner size="sm" />
          <span className="text-sm">{t('common.loading')}</span>
        </div>
      )}

      {query.isError && (
        <p role="alert" className="text-sm text-red p-6">
          {t('projects.error_load')}
        </p>
      )}

      {query.data && (
        <>
          {/* Zone A — p2-header */}
          <DetailHeader
            project={query.data}
            rowsCount={rowsCount}
            onAdd={() => send('add')}
            onImport={() => send('import')}
            onExport={() => setExportOpen(true)}
            onColumns={() => setColumnsOpen(true)}
            onImportZip={importZip.pick}
            importZipBusy={importZip.busy}
            onRename={() => setRenameOpen(true)}
            onDuplicate={onDuplicate}
            onToggleArchive={() => setArchiveOpen(true)}
            onDelete={() => setDeleteOpen(true)}
            menuDisabled={
              updateSettings.isPending ||
              deleteProj.isPending ||
              duplicateProj.isPending
            }
          />
          {/* Hidden <input type="file"> stays mounted at the page level so
              the ZIP-pick trigger inside the kebab menu can fire it. */}
          {importZip.hiddenInput}

          {/* Summary bar */}
          <SummaryBar counts={summary} />

          {/* Zone B — p2-launch-panel */}
          <div className="p2-launch-panel">
            <ProjectSettingsBar
              project={query.data}
              rowsCount={rowsCount}
              launchPending={launchPending}
              onValidate={() => send('validate')}
              onLaunchAudio={() => send('launch_audio')}
              onLaunchFull={() => send('launch_full')}
              onStopAll={() => send('stop')}
            />
          </div>

          {/* Zone C — prep table */}
          <div className="p2-section">
            <RowsTable
              projectId={query.data.id}
              projectName={query.data.title}
              projectElModel={query.data.el_model}
              projectFalStack={query.data.video_stack}
              externalSignal={tableSignal}
              onRowsCountChange={setRowsCount}
              onSummaryChange={setSummary}
              onLaunchPendingChange={setLaunchPending}
            />
          </div>

          {/* Zone D — tasks header + body */}
          <div className="p2-tasks-header">
            <span className="p2-tasks-title">
              {t('projects.p2.tasks_title', { defaultValue: 'Завдання та результати' })}
            </span>
          </div>
          <p className="p2-tasks-subtitle">
            {t('projects.p2.tasks_subtitle', {
              defaultValue:
                'Тут відображається виконання рядків проєкту: статуси, помилки, результати та дії.',
            })}
          </p>

          <div className="p2-section" style={{ marginBottom: 40 }}>
            <TasksSection projectId={query.data.id} />
          </div>

          <ExportProjectDialog
            open={exportOpen}
            projectId={query.data.id}
            rowsCount={rowsCount}
            onClose={() => setExportOpen(false)}
          />
          <ColumnsDialog
            open={columnsOpen}
            visibility={columnSettings.visibility}
            onClose={() => setColumnsOpen(false)}
            onSave={columnSettings.save}
            onReset={columnSettings.reset}
          />
          <RenameProjectDialog
            open={renameOpen}
            initialName={query.data.title}
            onClose={() => setRenameOpen(false)}
            onSave={onRenameSubmit}
            isPending={updateSettings.isPending}
          />
          <ConfirmDialog
            open={archiveOpen}
            onClose={() => setArchiveOpen(false)}
            onConfirm={onArchiveConfirm}
            isPending={updateSettings.isPending}
            title={
              query.data.status === 'archived'
                ? t('projects.actions.unarchive_title', { defaultValue: 'Розархівувати проєкт' })
                : t('projects.p2.archive_title', { defaultValue: 'Архівувати проєкт?' })
            }
            message={
              query.data.status === 'archived'
                ? t('projects.actions.unarchive_message', {
                    defaultValue: 'Повернути проєкт «{{name}}» у активні?',
                    name: query.data.title,
                  })
                : t('projects.p2.archive_body', {
                    defaultValue:
                      'Проєкт буде переміщено до архіву. Ви зможете відновити його пізніше.',
                  })
            }
            confirmLabel={
              query.data.status === 'archived'
                ? t('projects.actions.unarchive', { defaultValue: 'Розархівувати' })
                : t('projects.p2.btn_archive_confirm', { defaultValue: 'Архівувати' })
            }
          />
          <ConfirmDialog
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            onConfirm={onDeleteConfirm}
            isPending={deleteProj.isPending}
            destructive
            title={t('projects.p2.delete_proj_title', { defaultValue: 'Видалити проєкт?' })}
            message={t('projects.p2.delete_proj_body', {
              defaultValue:
                'Проєкт «{{title}}» буде видалено. Цю дію не можна скасувати.',
              title: query.data.title,
            })}
            confirmLabel={t('projects.p2.btn_delete_confirm', { defaultValue: 'Видалити' })}
          />
        </>
      )}
    </div>
  )
}

/* ───────────────────────────────────────────────────────────────
   Summary bar — .p2-summary
   ─────────────────────────────────────────────────────────────── */

export interface SummaryCounts {
  total: number
  ready: number
  running: number
  errors: number
  done: number
  empty: number
}

const emptySummary: SummaryCounts = {
  total: 0,
  ready: 0,
  running: 0,
  errors: 0,
  done: 0,
  empty: 0,
}

function SummaryBar({ counts }: { counts: SummaryCounts }) {
  const { t } = useTranslation()
  return (
    <div className="p2-summary">
      <SummaryItem
        label={t('projects.p2.sum_rows', { defaultValue: 'Рядків:' })}
        value={counts.total}
      />
      <SummaryItem
        label={t('projects.p2.sum_ready', { defaultValue: 'Готові:' })}
        value={counts.ready}
        tone="active"
      />
      <SummaryItem
        label={t('projects.p2.sum_running', { defaultValue: 'У роботі:' })}
        value={counts.running}
        tone="active"
      />
      <SummaryItem
        label={t('projects.p2.sum_errors', { defaultValue: 'Помилки:' })}
        value={counts.errors}
        tone="error"
      />
      <SummaryItem
        label={t('projects.p2.sum_done', { defaultValue: 'Готово:' })}
        value={counts.done}
        tone="done"
      />
      <SummaryItem
        label={t('projects.p2.sum_empty', { defaultValue: 'Незаповнені:' })}
        value={counts.empty}
      />
    </div>
  )
}

function SummaryItem({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: 'active' | 'error' | 'done'
}) {
  const cls =
    tone === 'active'
      ? 'p2-summary-item active'
      : tone === 'error'
        ? 'p2-summary-item error'
        : tone === 'done'
          ? 'p2-summary-item done'
          : 'p2-summary-item'
  return (
    <div className={cls}>
      <span>{label}</span>
      <span className="p2-summary-val">{value}</span>
    </div>
  )
}

/* ───────────────────────────────────────────────────────────────
   p2-header — back link / title / subtitle / action buttons
   ─────────────────────────────────────────────────────────────── */

function DetailHeader({
  project,
  rowsCount,
  onAdd,
  onImport,
  onExport,
  onColumns,
  onImportZip,
  importZipBusy,
  onRename,
  onDuplicate,
  onToggleArchive,
  onDelete,
  menuDisabled,
}: {
  project: Project
  rowsCount: number
  onAdd: () => void
  onImport: () => void
  onExport: () => void
  onColumns: () => void
  onImportZip: () => void
  importZipBusy?: boolean
  onRename: () => void
  onDuplicate: () => void
  onToggleArchive: () => void
  onDelete: () => void
  menuDisabled?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="p2-header">
      <div className="p2-header-left">
        <Link to="/projects" className="p2-back">
          ← {t('projects.p2.back_to_projects', { defaultValue: 'Проєкти' })}
        </Link>
        <div className="p2-title-row">
          <span className="p2-title" title={project.title}>
            {project.title}
          </span>
          <button
            type="button"
            className="p2-title-edit"
            onClick={onRename}
            aria-label={t('projects.p2.menu_rename', { defaultValue: 'Перейменувати' })}
            title={t('projects.p2.menu_rename', { defaultValue: 'Перейменувати' })}
          >
            ✎
          </button>
          {rowsCount === 0 && (
            <span className="p2-badge-empty">
              {t('projects.p2.badge_empty', { defaultValue: 'Порожній' })}
            </span>
          )}
        </div>
        <div className="p2-subtitle">
          {project.description ||
            t('projects.p2.subtitle_prep', {
              defaultValue: 'Підготовка контенту та запуск обробки',
            })}
        </div>
      </div>
      {/* Legacy-parity 5-button toolbar (project-v2.html lines 31-47):
          + Рядок | Імпорт | Експорт | Колонки | ⋯. Import ZIP is the
          only secondary action in the kebab menu — Sheets/Drive were
          rewrite-only fictions and are removed. */}
      <div className="p2-header-actions">
        <button className="p2-hdr-btn primary" onClick={onAdd}>
          + {t('projects.p2.btn_add_row', { defaultValue: 'Рядок' })}
        </button>
        <button className="p2-hdr-btn" onClick={onImport}>
          {t('projects.p2.btn_import', { defaultValue: 'Імпорт' })}
        </button>
        <button className="p2-hdr-btn" onClick={onExport}>
          {t('projects.p2.btn_export', { defaultValue: 'Експорт' })}
        </button>
        <button className="p2-hdr-btn" onClick={onColumns}>
          {t('projects.p2.btn_columns', { defaultValue: 'Колонки' })}
        </button>
        <ProjectActionsMenu
          status={project.status}
          onRename={onRename}
          onDuplicate={onDuplicate}
          onToggleArchive={onToggleArchive}
          onDelete={onDelete}
          onImportZip={onImportZip}
          importZipBusy={importZipBusy}
          disabled={menuDisabled}
        />
      </div>
    </div>
  )
}

// Re-export for RowsTable to use the same shape when surfacing summary
// to the page header.
export type { SummaryCounts as SummaryCountsType }
